import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

export default function Memberships() {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyModal, setApplyModal] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({
    fullName: '', phone: '', email: '', address: '',
    telegramUsername: '', whatsappNumber: '', chimeTag: ''
  });

  useEffect(() => {
    api.get('/memberships').then(({ data }) => setMemberships(data.memberships || [])).finally(() => setLoading(false));
    api.get('/payments').then(({ data }) => setPaymentMethods(data.methods || [])).catch(() => {});
  }, [user]);
  useEffect(() => {
    api.get('/payments').then(({ data }) => setPaymentMethods(data.methods || [])).catch(() => {});
  }, []);

  const openApply = (m) => {
    setApplyModal(m);
    setForm({ fullName: user?.profile?.firstName ? `${user.profile.firstName} ${user.profile.lastName}` : '', phone: '', email: user?.email || '', address: '', telegramUsername: '', whatsappNumber: '', chimeTag: '' });
    setSelectedPayment(null);
    setScreenshot(null);
    setScreenshotPreview(null);
    setMessage(null);
  };

  const handleScreenshot = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshot(reader.result);
      setScreenshotPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.fullName || !form.phone || !form.email || !form.address) {
      setMessage({ type: 'error', text: 'Please fill all required fields!' });
      return;
    }
    if (applyModal.price > 0 && !selectedPayment) {
      setMessage({ type: 'error', text: 'Please select a payment method!' });
      return;
    }
    if (applyModal.price > 0 && !screenshot) {
      setMessage({ type: 'error', text: 'Please upload payment screenshot!' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/memberships/${applyModal._id}/apply`, {
        ...form,
        paymentMethod: selectedPayment?.name || '',
        paymentScreenshot: screenshot || '',
      });
      setMessage({ type: 'success', text: '✅ Application submitted! We will review it shortly.' });
      setTimeout(() => { setApplyModal(null); setMessage(null); }, 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Error submitting application' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ paddingTop: 70 }}>
      <div className="page-header">
        <h1>MEMBERSHIPS</h1>
        <p>Choose a plan that fits your gaming lifestyle</p>
      </div>
      <div className="container" style={{ paddingBottom: 80 }}>
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : memberships.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>💎</div>
            <p style={{ fontSize: 18 }}>Membership plans coming soon!</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 24, maxWidth: 1000, margin: '0 auto' }}>
            {memberships.map(m => {
              const userId = user?.id || user?._id; const userApp = userId ? m.applicants?.find(a => String(a.user) === String(userId)) : null;
              return (
                <div key={m._id} className="card fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 20, border: m.isPopular ? `2px solid ${m.color || 'var(--accent-cyan)'}` : '1px solid var(--border)', position: 'relative', overflow: 'hidden' }}>
                  {m.isPopular && (
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: m.color || 'var(--accent-cyan)', padding: '6px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: 'var(--font-display)', letterSpacing: '0.1em' }}>MOST POPULAR</div>
                  )}
                  <div style={{ marginTop: m.isPopular ? 36 : 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22 }}>{m.name}</h3>
                      <span style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 12px', fontSize: 13, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{m.type}</span>
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: 42, fontWeight: 900, color: m.color || 'var(--accent-cyan)' }}>{m.price === 0 ? 'FREE' : `$${m.price}`}</span>
                      {m.price > 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 15 }}>/{m.type === 'weekly' ? 'week' : m.type === 'monthly' ? 'month' : 'year'}</span>}
                    </div>
                    {m.features?.length > 0 && (
                      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {m.features.map((f, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, color: 'var(--text-secondary)', fontSize: 14 }}>
                            <span style={{ color: m.color || 'var(--accent-cyan)', flexShrink: 0, marginTop: 2 }}>✓</span>
                            {f}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {userApp ? (
                    <div style={{ padding: '10px 14px', borderRadius: 8, textAlign: 'center', fontSize: 13, fontWeight: 700,
                      background: userApp.status === 'approved' ? 'rgba(16,185,129,0.1)' : userApp.status === 'rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(245,196,0,0.1)',
                      color: userApp.status === 'approved' ? '#10b981' : userApp.status === 'rejected' ? '#ef4444' : '#f5c400',
                      border: `1px solid ${userApp.status === 'approved' ? 'rgba(16,185,129,0.3)' : userApp.status === 'rejected' ? 'rgba(239,68,68,0.3)' : 'rgba(245,196,0,0.3)'}`
                    }}>
                      {userApp.status === 'approved' ? '✅ Membership Active!' : userApp.status === 'rejected' ? '❌ Application Rejected' : '⏳ Application Pending Review'}
                    </div>
                  ) : user ? (
                    <button className="btn btn-primary" onClick={() => openApply(m)} style={{ marginTop: 'auto' }}>
                      {m.price === 0 ? '🎁 Apply Free' : `💳 Apply Now — $${m.price}`}
                    </button>
                  ) : (
                    <Link to="/login" className="btn btn-primary" style={{ marginTop: 'auto', textAlign: 'center' }}>Login to Apply</Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Application Modal */}
      {applyModal && (
        <div className="modal-overlay" onClick={() => setApplyModal(null)}>
          <div className="modal" style={{ maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setApplyModal(null)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 6, color: applyModal.color || 'var(--accent-cyan)' }}>
              Apply for {applyModal.name}
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>
              {applyModal.price === 0 ? '🎁 Free membership — fill in your details below' : `💳 $${applyModal.price}/${applyModal.type} — Fill details and complete payment`}
            </p>

            {message && (
              <div style={{ background: message.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`, borderRadius: 8, padding: '10px 14px', color: message.type === 'success' ? '#10b981' : '#ef4444', marginBottom: 16, fontSize: 14 }}>
                {message.text}
              </div>
            )}

            {/* Required Fields */}
            <div style={{ background: 'rgba(245,196,0,0.05)', border: '1px solid rgba(245,196,0,0.2)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#f5c400', fontFamily: 'var(--font-display)', marginBottom: 12 }}>REQUIRED INFORMATION</div>
              <div className="form-group">
                <label>Full Name *</label>
                <input value={form.fullName} onChange={e => setForm({...form, fullName: e.target.value})} placeholder="Your full name" />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Your phone number" />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Your email address" />
              </div>
              <div className="form-group">
                <label>Full Address with Zip Code *</label>
                <textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Street, City, State, Zip Code" rows={2} style={{ width: '100%', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text-primary)', resize: 'vertical' }} />
              </div>
            </div>

            {/* Optional Fields */}
            <div style={{ background: 'rgba(100,100,100,0.05)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', marginBottom: 12 }}>OPTIONAL CONTACT INFO</div>
              <div className="form-group">
                <label>Telegram Username / Number</label>
                <input value={form.telegramUsername} onChange={e => setForm({...form, telegramUsername: e.target.value})} placeholder="@username or phone number" />
              </div>
              <div className="form-group">
                <label>WhatsApp Number</label>
                <input value={form.whatsappNumber} onChange={e => setForm({...form, whatsappNumber: e.target.value})} placeholder="WhatsApp phone number" />
              </div>
              <div className="form-group">
                <label>Chime Tag / Cash Tag / PayPal Tag</label>
                <input value={form.chimeTag} onChange={e => setForm({...form, chimeTag: e.target.value})} placeholder="$yourTag" />
              </div>
            </div>

            {/* Payment Section - only for paid memberships */}
            {applyModal.price > 0 && (
              <div style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#10b981', fontFamily: 'var(--font-display)', marginBottom: 12 }}>PAYMENT — ${applyModal.price}</div>
                
                {/* Payment Methods */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>Select Payment Method *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {paymentMethods.length > 0 ? paymentMethods.map(pm => (
                      <button key={pm._id} onClick={() => setSelectedPayment(pm)}
                        style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${selectedPayment?._id === pm._id ? '#10b981' : 'var(--border)'}`, background: selectedPayment?._id === pm._id ? 'rgba(16,185,129,0.1)' : 'var(--bg-secondary)', color: selectedPayment?._id === pm._id ? '#10b981' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        {pm.name}
                      </button>
                    )) : (
                      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No payment methods available</p>
                    )}
                  </div>
                </div>

                {/* QR Code */}
                {selectedPayment && (
                  <div style={{ textAlign: 'center', marginBottom: 16, padding: 16, background: 'var(--bg-secondary)', borderRadius: 10 }}>
                    {selectedPayment.qrCode ? (
                      <img src={`https://officialapexgaming.com${selectedPayment.qrCode}`} alt="QR Code" style={{ maxWidth: 200, borderRadius: 8 }} />
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{selectedPayment.name}</div>
                        {selectedPayment.tag && <div>Tag: <strong>{selectedPayment.tag}</strong></div>}
                        {selectedPayment.address && <div>Address: <strong>{selectedPayment.address}</strong></div>}
                        {selectedPayment.instructions && <div style={{marginTop:6,color:'var(--text-muted)'}}>{selectedPayment.instructions}</div>}
                      </div>
                    )}
                    <div style={{ marginTop: 8, fontSize: 13, color: '#f5c400', fontWeight: 700 }}>Send ${applyModal.price} and upload screenshot below</div>
                  </div>
                )}

                {/* Screenshot Upload */}
                <div className="form-group">
                  <label>Payment Screenshot *</label>
                  <input type="file" accept="image/*" onChange={handleScreenshot} style={{ color: 'var(--text-secondary)' }} />
                  {screenshotPreview && (
                    <img src={screenshotPreview} alt="Screenshot" style={{ marginTop: 8, maxWidth: '100%', borderRadius: 8, maxHeight: 200, objectFit: 'contain' }} />
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={handleSubmit} disabled={submitting} className="btn btn-primary" style={{ flex: 1 }}>
                {submitting ? '⟳ Submitting...' : '✅ Submit Application'}
              </button>
              <button onClick={() => setApplyModal(null)} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
